import { useState } from "react";
import { Link } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useWhatsAppOrder } from "@/hooks/useWhatsAppOrder";

const faqItems = [
  {
    id: "fabric",
    question: "What fabric is the kurta set made of?",
    answer:
      "Soft, breathable 100% cotton, comfortable for all-day wear.",
  },
  {
    id: "sizes",
    question: "What sizes are available?",
    answer:
      "M, L, XL, and XXL. Check the size details on the product page before ordering.",
  },
  {
    id: "pyjama",
    question: "Is the pyjama included?",
    answer:
      "Yes — every set is a 2-piece kurta with matching pyjama bottoms.",
  },
  {
    id: "delivery",
    question: "How long does delivery take?",
    answer:
      "Orders dispatch within 1–2 days and typically arrive within 5–7 days across India.",
  },
  {
    id: "shipping-charge",
    question: "What is the shipping charge?",
    answer:
      "A flat ₹30 on every order, anywhere in India.",
  },
  {
    id: "cod",
    question: "Can I pay cash on delivery (COD)?",
    answer:
      "Online prepaid is the fastest. For COD, message us on WhatsApp and we'll assist.",
  },
  {
    id: "returns",
    question: "What is your return policy?",
    answer:
      "Easy 7-day returns, plus exchange for size issues or defects. Items must be unused with tags.",
  },
  {
    id: "tracking",
    question: "How do I track my order?",
    answer:
      "Once dispatched, you'll receive tracking details. Any questions, reach us on WhatsApp.",
  },
];

function AccordionItem({
  item,
  isOpen,
  onToggle,
}: {
  item: (typeof faqItems)[0];
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-background-200/70 rounded-xl overflow-hidden bg-background-50">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-4 md:py-5 text-left cursor-pointer hover:bg-background-100/60 transition-colors"
      >
        <span className="font-heading text-base md:text-lg font-semibold text-accent-800 pr-2">
          {item.question}
        </span>
        <div
          className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-700 transition-transform duration-300 ${
            isOpen ? "rotate-45" : ""
          }`}
        >
          <i className="ri-add-line w-4 h-4 flex items-center justify-center text-base" />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-5 md:px-7 pb-4 md:pb-5 text-sm md:text-base text-foreground-600 leading-relaxed">
          {item.answer}
        </div>
      </div>
    </div>
  );
}

export default function Faq() {
  const [openId, setOpenId] = useState<string | null>(null);
  const { openModal } = useWhatsAppOrder();

  const handleToggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <div>
      <PageBanner
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about our kurta sets, shipping, and returns."
        bgImage="/images/faq-banner.jpg"
      />

      <section className="py-10 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col gap-3 md:gap-4">
              {faqItems.map((item) => (
                <AccordionItem
                  key={item.id}
                  item={item}
                  isOpen={openId === item.id}
                  onToggle={() => handleToggle(item.id)}
                />
              ))}
            </div>

            <div className="mt-10 md:mt-12 text-center">
              <p className="text-foreground-600 text-sm leading-relaxed">
                Still have questions?{" "}
                <button
                  type="button"
                  onClick={() => openModal()}
                  className="text-accent-600 hover:text-accent-700 font-medium underline underline-offset-2 cursor-pointer"
                >
                  Message us on WhatsApp
                </button>{" "}
                or{" "}
                <Link
                  to="/contact"
                  className="text-accent-600 hover:text-accent-700 font-medium underline underline-offset-2"
                >
                  fill out our contact form
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}